import React, { useEffect, useState } from "react";
import axios from "../../services/api";
import ReactPaginate from "react-paginate";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

/* ── PO status badge ── */
const poStatusBadge = (status) => {
  const map = {
    "Pending":                "ims-badge-pending",
    "Approved by Principal":  "ims-badge-principal",
    "Approved by Management": "ims-badge-approved",
    "Rejected":               "ims-badge-rejected",
    "GRN Created":            "ims-badge-issued",
    "Partially Received":     "ims-badge-partial",
    "Fully Received":         "ims-badge-full",
  };
  return (
    <span className={`ims-badge ${map[status] || "ims-badge-notcreated"}`}>
      {status}
    </span>
  );
};

const PurchaseOrderList = () => {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`/purchase-orders`, {
        params: {
          page: currentPage + 1,
          limit: itemsPerPage,
          search: searchTerm,
          user_id: user.id,
          role: user.role,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data.orders || []);
      setTotalPages(response.data.totalPages || 0);
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage, searchTerm, itemsPerPage]);

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(orders);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PurchaseOrders");
    XLSX.writeFile(workbook, "PurchaseOrders.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Purchase Orders", 14, 15);
    const tableData = orders.map((order, i) => [
      i + 1,
      order.id,
      order.vendor_name,
      order.order_date,
      order.status,
      order.grand_total,
    ]);
    autoTable(doc, {
      startY: 20,
      head: [["Sr", "PO ID", "Vendor", "Order Date", "Status", "Total"]],
      body: tableData,
      theme: "grid",
    });
    doc.save("PurchaseOrders.pdf");
  };

  const generatePurchaseOrderPDF = async (id) => {
    try {
      const response = await axios.get(`/purchase-orders/${id}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF");
    }
  };

  const downloadAttachment = async (order) => {
    try {
      const response = await axios.get(`/purchase-orders/${order.id}/attachment`, {
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = order.attachment_original_name || "Purchase_Order_Attachment.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading PO attachment:", error);
      alert("Failed to download PDF attachment");
    }
  };

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>Purchase Orders</h4>
          <p>Manage and track all purchase orders</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-success" onClick={exportToExcel}>
            <i className="fas fa-file-excel mr-1"></i> Excel
          </button>
          <button className="btn btn-sm btn-danger" onClick={exportToPDF}>
            <i className="fas fa-file-pdf mr-1"></i> PDF
          </button>
          {user.role === "store_manager" && (
            <button
              className="btn btn-sm btn-primary"
              onClick={() => navigate("/dashboard/create-purchase-order")}
            >
              <i className="fas fa-plus mr-1"></i> New PO
            </button>
          )}
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="row mb-3 align-items-center">
        <div className="col-md-4 d-flex align-items-center">
          <label className="mr-2 mb-0" style={{ whiteSpace: "nowrap", fontSize: "13px" }}>Show:</label>
          <select
            className="form-control form-control-sm w-auto"
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(0); }}
          >
            {[10, 20, 50, 100].map((num) => (
              <option key={num}>{num}</option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <input
            type="text"
            placeholder="Search purchase orders..."
            className="form-control form-control-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="table-responsive">
        <table className="ims-table">
          <thead>
            <tr>
              <th>Sr</th>
              <th>Order Date</th>
              <th>PO No.</th>
              {user.role !== "store_manager" && <th>Created By</th>}
              <th>Vendor</th>
              <th>Grand Total</th>
              <th>Status</th>
              <th style={{ width: "70px", textAlign: "center" }}>PDF</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length > 0 ? (
              orders.map((order, i) => (
                <tr key={order.id}>
                  <td>{i + 1 + currentPage * itemsPerPage}</td>
                  <td>{format(new Date(order.order_date), "dd-MM-yyyy")}</td>
                  <td><strong>{order.po_number}</strong></td>
                  {user.role !== "store_manager" && <td>{order.created_by_name}</td>}
                  <td>{order.vendor_name}</td>
                  <td style={{ fontWeight: 600 }}>₹{Number(order.grand_total).toLocaleString("en-IN")}</td>
                  <td>{poStatusBadge(order.status)}</td>
                  <td style={{ textAlign: "center" }}>
                    {order.attachment_path ? (
                      <button
                        className="ims-btn ims-btn-pdf"
                        title={`Download ${order.attachment_original_name || "attached PDF"}`}
                        onClick={() => downloadAttachment(order)}
                      >
                        <i className="fas fa-download"></i>
                      </button>
                    ) : null}
                  </td>
                  <td>
                    {/* View */}
                    <button
                      className="ims-btn ims-btn-view"
                      title="View"
                      onClick={() => navigate(`/dashboard/view-purchase-order/${order.id}`)}
                    >
                      <i className="fas fa-eye"></i>
                    </button>

                    {/* Print PDF — Approved by Management */}
                    {order.status === "Approved by Management" && (
                      <button
                        className="ims-btn ims-btn-pdf"
                        title="Print PDF"
                        onClick={() => generatePurchaseOrderPDF(order.id)}
                      >
                        <i className="fas fa-print"></i>
                      </button>
                    )}

                    {/* Create GRN — store_manager + Approved by Management */}
                    {user.role === "store_manager" && order.status === "Approved by Management" && (
                      <button
                        className="ims-btn ims-btn-grn"
                        title="Create GRN"
                        onClick={() => navigate(`/dashboard/create-grn/${order.id}`)}
                      >
                        <i className="fas fa-file-invoice"></i>
                      </button>
                    )}

                    {/* Edit — store_manager + Pending */}
                    {user.role === "store_manager" && order.status === "Pending" && (
                      <button
                        className="ims-btn ims-btn-edit"
                        title="Edit"
                        onClick={() => navigate(`/dashboard/edit-purchase-order/${order.id}`)}
                      >
                        <i className="fas fa-pen"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={user.role !== "store_manager" ? 9 : 8} className="text-center text-muted py-4">
                  No purchase orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <ReactPaginate
        previousLabel={"← Previous"}
        nextLabel={"Next →"}
        breakLabel={"..."}
        pageCount={totalPages}
        marginPagesDisplayed={2}
        pageRangeDisplayed={5}
        onPageChange={handlePageClick}
        containerClassName={"pagination justify-content-center mt-3"}
        pageClassName={"page-item"}
        pageLinkClassName={"page-link"}
        previousClassName={"page-item"}
        previousLinkClassName={"page-link"}
        nextClassName={"page-item"}
        nextLinkClassName={"page-link"}
        breakClassName={"page-item"}
        breakLinkClassName={"page-link"}
        activeClassName={"active"}
      />
    </div>
  );
};

export default PurchaseOrderList;
